import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Loader2, ExternalLink, AlertTriangle } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";

import api from "../../services/api";
import toastError from "../../errors/toastError";

/**
 * Põe o contato da conversa no funil, sem sair do atendimento.
 *
 * Antes, quem descobria uma oportunidade no meio do papo tinha de abrir o CRM
 * noutra aba, procurar o contato pelo número e cadastrar na mão — e por isso
 * quase ninguém cadastrava.
 */
const AddToFunnelModal = ({ open, onClose, contact, ticketId }) => {
	const navigate = useNavigate();

	const [pipelines, setPipelines] = useState([]);
	const [pipelineId, setPipelineId] = useState("");
	const [stageId, setStageId] = useState("");
	const [title, setTitle] = useState("");
	const [value, setValue] = useState("");
	const [salvando, setSalvando] = useState(false);
	const [carregando, setCarregando] = useState(true);

	// Negócio que este contato já tem. Cadastrar de novo criaria duas fichas
	// da mesma pessoa em etapas diferentes, e ninguém saberia qual vale.
	const [existente, setExistente] = useState(null);

	const carregar = useCallback(async () => {
		if (!open || !contact?.id) return;

		setCarregando(true);
		try {
			const [resPipelines, resDeals] = await Promise.all([
				api.get("/crm/pipelines"),
				api.get("/crm/deals", { params: { contactId: contact.id } }),
			]);

			const lista = resPipelines.data || [];
			setPipelines(lista);

			const jaTem = (resDeals.data?.deals || resDeals.data || []).find(
				(d) => String(d.contactId) === String(contact.id)
			);
			setExistente(jaTem || null);

			if (lista.length > 0) {
				const primeiro = lista[0];
				setPipelineId(String(primeiro.id));
				const etapas = [...(primeiro.stages || [])].sort(
					(a, b) => (a.position || 0) - (b.position || 0)
				);
				if (etapas.length) setStageId(String(etapas[0].id));
			}

			setTitle(contact.name || "");
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [open, contact]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const funilEscolhido = pipelines.find(
		(p) => String(p.id) === String(pipelineId)
	);
	const etapas = [...(funilEscolhido?.stages || [])].sort(
		(a, b) => (a.position || 0) - (b.position || 0)
	);

	// Trocar de funil precisa trocar a etapa junto: etapa é filha do funil, e
	// manter a anterior mandaria o negócio para uma etapa de outro funil.
	const trocarFunil = (novo) => {
		setPipelineId(novo);
		const alvo = pipelines.find((p) => String(p.id) === String(novo));
		const primeiras = [...(alvo?.stages || [])].sort(
			(a, b) => (a.position || 0) - (b.position || 0)
		);
		setStageId(primeiras.length ? String(primeiras[0].id) : "");
	};

	const salvar = async (e) => {
		e.preventDefault();
		setSalvando(true);
		try {
			const { data } = await api.post("/crm/deals", {
				pipelineId: +pipelineId,
				stageId: +stageId,
				contactId: contact.id,
				title: title.trim() || contact.name,
				value: value ? Number(String(value).replace(",", ".")) : 0,
			});

			toast.success("Contato adicionado ao funil.");
			onClose();
			// Leva direto para o funil: o objetivo de quem clicou era colocar a
			// oportunidade lá, e conferir é o passo seguinte natural.
			navigate("/crm/funnel");
			return data;
		} catch (err) {
			toastError(err);
		}
		setSalvando(false);
		return null;
	};

	return (
		<Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
			<DialogContent className="sm:max-w-md">
				<form onSubmit={salvar}>
					<DialogHeader>
						<DialogTitle>Adicionar ao funil</DialogTitle>
						<DialogDescription>
							Cria uma oportunidade no CRM para {contact?.name || "este contato"}
							, sem sair da conversa.
						</DialogDescription>
					</DialogHeader>

					{carregando ? (
						<div className="py-8 text-center">
							<Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
						</div>
					) : existente ? (
						<div className="space-y-3 py-4">
							<p className="flex gap-1.5 rounded-md bg-muted p-3 text-sm leading-relaxed">
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								Este contato já está no funil como
								<span className="font-medium">“{existente.title}”</span>. Criar
								de novo deixaria duas fichas da mesma pessoa.
							</p>
							<Button
								type="button"
								variant="outline"
								className="w-full"
								onClick={() => {
									onClose();
									navigate("/crm/funnel");
								}}
							>
								<ExternalLink className="h-4 w-4" />
								Abrir no funil
							</Button>
						</div>
					) : pipelines.length === 0 ? (
						<p className="py-6 text-center text-sm text-muted-foreground">
							Nenhum funil cadastrado ainda. Crie um em Funil de vendas.
						</p>
					) : (
						<div className="grid gap-3 py-4">
							<div>
								<Label htmlFor="funil">Funil</Label>
								<Select value={pipelineId} onValueChange={trocarFunil}>
									<SelectTrigger id="funil">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										{pipelines.map((p) => (
											<SelectItem key={p.id} value={String(p.id)}>
												{p.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label htmlFor="etapa">Etapa</Label>
								<Select value={stageId} onValueChange={setStageId}>
									<SelectTrigger id="etapa">
										<SelectValue placeholder="Escolha a etapa" />
									</SelectTrigger>
									<SelectContent>
										{etapas.map((s) => (
											<SelectItem key={s.id} value={String(s.id)}>
												{s.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{etapas.length === 0 && (
									<p className="mt-1 text-xs text-muted-foreground">
										Este funil ainda não tem etapas.
									</p>
								)}
							</div>

							<div>
								<Label htmlFor="titulo">Do que se trata</Label>
								<Input
									id="titulo"
									required
									value={title}
									onChange={(e) => setTitle(e.target.value)}
								/>
							</div>

							<div>
								<Label htmlFor="valor">
									Valor{" "}
									<span className="font-normal text-muted-foreground">
										(opcional)
									</span>
								</Label>
								<Input
									id="valor"
									inputMode="decimal"
									placeholder="0,00"
									value={value}
									onChange={(e) => setValue(e.target.value)}
								/>
							</div>
						</div>
					)}

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={onClose}>
							Fechar
						</Button>
						{!existente && pipelines.length > 0 && (
							<Button
								type="submit"
								disabled={salvando || carregando || !stageId}
							>
								{salvando && <Loader2 className="h-4 w-4 animate-spin" />}
								Adicionar
							</Button>
						)}
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default AddToFunnelModal;
